import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Plus, X, Loader2, ArrowLeft, Check, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const EditPrompts = () => {
  const [availablePrompts, setAvailablePrompts] = useState([]);
  const [userPrompts, setUserPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPromptPicker, setShowPromptPicker] = useState(false);

  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [promptsRes, userPromptsRes] = await Promise.all([
          axios.get(`${API}/prompts`),
          axios.get(`${API}/profile/prompts`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setAvailablePrompts(promptsRes.data.prompts);
        setUserPrompts(userPromptsRes.data.prompts || []);
      } catch (err) {
        toast.error('Kunne ikke hente spørgsmål');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const addPrompt = (promptId) => {
    if (userPrompts.length >= 3) {
      toast.error('Du kan kun have 3 spørgsmål');
      return;
    }

    const prompt = availablePrompts.find(p => p.id === promptId);
    if (prompt && !userPrompts.find(p => p.prompt_id === promptId)) {
      setUserPrompts([...userPrompts, { prompt_id: promptId, question: prompt.question, answer: '' }]);
    }
    setShowPromptPicker(false);
  };

  const removePrompt = (promptId) => {
    setUserPrompts(userPrompts.filter(p => p.prompt_id !== promptId));
  };

  const updateAnswer = (promptId, answer) => {
    setUserPrompts(userPrompts.map(p => 
      p.prompt_id === promptId ? { ...p, answer } : p
    ));
  };

  const handleSave = async () => {
    // Validate that all prompts have answers
    const emptyPrompts = userPrompts.filter(p => !p.answer.trim());
    if (emptyPrompts.length > 0) {
      toast.error('Udfyld venligst alle svar');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/profile/prompts`, 
        { prompts: userPrompts.map(p => ({ prompt_id: p.prompt_id, answer: p.answer })) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Profil-spørgsmål gemt!');
      navigate(-1);
    } catch (err) {
      toast.error('Kunne ikke gemme spørgsmål');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const usedPromptIds = new Set(userPrompts.map(p => p.prompt_id));
  const unusedPrompts = availablePrompts.filter(p => !usedPromptIds.has(p.id));

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-lg mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <MessageSquare className="w-6 h-6 text-primary" />
                <span className="text-lg font-display font-semibold text-foreground">Profil-spørgsmål</span>
              </div>
            </div>
            <Button 
              onClick={handleSave} 
              disabled={saving || userPrompts.length === 0}
              className="rounded-full"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
              Gem
            </Button>
          </div>
        </div>
      </div>

      <div className="pt-20 px-4 pb-8">
        <div className="max-w-lg mx-auto">
          {/* Info */}
          <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-2xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <h3 className="font-semibold text-foreground">Gør din profil mere interessant</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Tilføj op til 3 spørgsmål for at vise din personlighed og give andre noget at snakke om.
                </p>
              </div>
            </div>
          </div>

          {/* Current Prompts */}
          <div className="space-y-4 mb-6">
            <AnimatePresence mode="popLayout">
              {userPrompts.map((prompt, index) => (
                <motion.div
                  key={prompt.prompt_id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="bg-card rounded-2xl border border-border p-4"
                >
                  <div className="flex items-start justify-between mb-3">
                    <p className="text-sm font-medium text-primary">{prompt.question}</p>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removePrompt(prompt.prompt_id)}
                      className="rounded-full h-8 w-8 -mt-1 -mr-1"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <Textarea
                    value={prompt.answer}
                    onChange={(e) => updateAnswer(prompt.prompt_id, e.target.value)}
                    placeholder="Skriv dit svar her..."
                    className="min-h-[80px] resize-none"
                    maxLength={300}
                  />
                  <p className="text-xs text-muted-foreground text-right mt-1">
                    {prompt.answer.length}/300
                  </p>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Add Prompt Button */}
          {userPrompts.length < 3 && (
            <Button
              variant="outline"
              onClick={() => setShowPromptPicker(true)}
              className="w-full rounded-xl border-dashed border-2 h-16"
            >
              <Plus className="w-5 h-5 mr-2" />
              Tilføj spørgsmål ({userPrompts.length}/3)
            </Button>
          )}
        </div>
      </div>

      {/* Prompt Picker Modal */}
      <AnimatePresence>
        {showPromptPicker && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 flex items-end justify-center"
            onClick={() => setShowPromptPicker(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="bg-card rounded-t-3xl w-full max-w-lg max-h-[70vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-border">
                <div className="w-12 h-1 bg-muted rounded-full mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground text-center">Vælg et spørgsmål</h3>
              </div>
              <div className="p-4 overflow-y-auto max-h-[50vh]">
                <div className="space-y-2">
                  {unusedPrompts.map((prompt) => (
                    <button
                      key={prompt.id}
                      onClick={() => addPrompt(prompt.id)}
                      className="w-full text-left p-4 bg-muted/50 hover:bg-muted rounded-xl transition-colors"
                    >
                      <p className="text-foreground">{prompt.question}</p>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EditPrompts;
